import { gql } from '@apollo/client';
import {
  USER_BASIC_FIELDS,
  ID_NAME_FIELDS,
  COUNTRY_FIELDS,
  ID_NAME_WEBSITE_LOGO_FIELDS,
  PROPERTY_MAIN_FIELDS,
} from 'graphql/common/fragments';

const COMMON_INDUSTRIAL_IOS_FIELDS = `
    id
    amps
    businessParkType
    craneServed
    craneCapacity
    hookHeight
    hookHeightMt
    transportationConnectivity
    loadingDocks
    typicalClearHeightMin
    typicalClearHeightMinMt
    typicalClearHeightMax
    typicalClearHeightMaxMt
    driveInDoors
    trailerParkingSpaces
    outsideStorage
    heavyPower
    sprinklers
    buildingDepth
    buildingDepthMt
    outsideStorageAcres
    outsideStorageHectares
    railServed {
      ${ID_NAME_FIELDS}
    }
    railProvider {
      ${ID_NAME_FIELDS}
    }
    dockConfigurationType {
      ${ID_NAME_FIELDS}
    }
`;

export const INDUSTRIAL_FRAGMENT = gql`
  fragment industrialFragment on Industrial {
    ${COMMON_INDUSTRIAL_IOS_FIELDS}
  }
`;

export const IOS_FRAGMENT = gql`
  fragment iosFragment on IOS {
    ${COMMON_INDUSTRIAL_IOS_FIELDS}
    actualUsable
    outsideStorageAcres
    outsideStorageHectares
    isStacking
    stackingHeight
  }
`;

const PROPERTY_COUNTRY_FIELDS = `
    ${COUNTRY_FIELDS}
    lengthMeasurement
`;

export const PROPERTY_COUNTRY_FRAGMENT = gql`
  fragment propertyCountryFragment on Country {
    ${PROPERTY_COUNTRY_FIELDS}
  }
`;

const EXPENSES_FIELDS = `
    id
    tax
    operatingExpenses
    taxAndOpex
    year
    timeMeasurement
`;

export const EXPENSES_FRAGMENT = gql`
  fragment expensesFragment on Expense {
    ${EXPENSES_FIELDS}
  }
`;

export const DATA_CENTER_FRAGMENT = gql`
  fragment dataCenterFragment on DataCenter {
    id
    createdAt
    updatedAt
    dataCenterBuildOutType {
      ${ID_NAME_FIELDS}
    }
    fiberLinesCompanies {
      ${ID_NAME_FIELDS}
    }
    cloudServicesCompanies {
      ${ID_NAME_FIELDS}
    }
    typicalCeilingHeightFeet
    typicalCeilingHeightInches
    typicalCeilingHeightMt
    underConstPower
    underConstSf
    underConstMt
    plannedPower
    plannedSf
    plannedMt
    commissionedPower
    commissionedSf
    commissionedMt
    costPerKwhMin
    costPerKwhMax
    feedType
    hasOnsiteSubstation
    powerProviderCompany {
      ${ID_NAME_FIELDS}
    }
    powerUseEfficiency
    numSubstationsServingFacility
    raisedFloorHeight
    raisedFloorHeightMt
    ups
    upsDescription
    generator
    generatorDescription
    pdu
    pduDescription
    crah
    crahDescription
    cooling
    totalPower
    retrofitYear
  }
`;

export const LEASING_COMPANY_FRAGMENT = gql`
  fragment leasingCompanyFragment on Company {
    ${ID_NAME_WEBSITE_LOGO_FIELDS}
  }
`;

export const OWNERS_FRAGMENT = gql`
  fragment ownersFragment on Company {
    ${ID_NAME_WEBSITE_LOGO_FIELDS}
  }
`;

export const LONG_LEASE_HOLDERS_FRAGMENT = gql`
  fragment longLeaseHoldersFragment on Company {
    ${ID_NAME_WEBSITE_LOGO_FIELDS}
  }
`;

export const DEVELOPMENT_COMPANIES_FRAGMENT = gql`
  fragment developmentCompaniesFragment on Company {
    ${ID_NAME_WEBSITE_LOGO_FIELDS}
  }
`;

export const MANAGEMENT_COMPANY_FRAGMENT = gql`
  fragment managementCompanyFragment on Company {
    ${ID_NAME_WEBSITE_LOGO_FIELDS}
  }
`;

export const OPERATOR_COMPANY_FRAGMENT = gql`
  fragment operatorCompanyFragment on Company {
    ${ID_NAME_WEBSITE_LOGO_FIELDS}
  }
`;

export const LIFE_SCIENCE_FRAGMENT = gql`
  fragment lifeScienceFragment on LifeScience {
    dockConfigurationType {
      ${ID_NAME_FIELDS}
    }
    loadingDocks
    driveInDoors
    typicalClearHeightMin
    typicalClearHeightMax
    conversion
    typicalCeilingHeightFeet
    typicalCeilingHeightInches
    typicalCeilingHeightMt
  }
`;

export const CREATED_USER_FRAGMENT = gql`
  fragment createdUserFragment on User {
    ${USER_BASIC_FIELDS}
  }
`;

export const UPDATED_USER_FRAGMENT = gql`
  fragment updatedUserFragment on User {
    ${USER_BASIC_FIELDS}
  }
`;

const EXPENSE_FIELDS = `
    tax
    operatingExpenses
    taxAndOpex
    year
    timeMeasurement
`;

export const EXPENSE_FRAGMENT = gql`
  fragment expenseFragment on Expense {
    ${EXPENSE_FIELDS}
  }
`;

export const HEALTHCARE_FRAGMENT = gql`
  fragment healthcareFragment on Healthcare {
    id
    numBeds
    providerCompanies {
      ${ID_NAME_FIELDS}
    }
  }
`;

export const MULTIFAMILY_FRAGMENT = gql`
  fragment multifamilyFragment on Multifamily {
    id
    totalUnits
    avgUnitSizeMt
    avgUnitSizeSf
    unitsZeroBed
    unitsOneBed
    unitsTwoBed
    unitsThreeBed
  }
`;

export const OFFICE_FRAGMENT = gql`
  fragment officeFragment on Office {
    typicalCeilingHeightFeet
    typicalCeilingHeightInches
    typicalCeilingHeightMt
    storiesAboveGround
    storiesBelowGround
  }
`;

export const PROPERTY_CLICKTHROUGHS_FRAGMENT = gql`
  fragment propertyClickthroughsFragment on Property {
    ${PROPERTY_MAIN_FIELDS}
    propertyCountry {
      ...propertyCountryFragment
    }
    propertyType {
      ${ID_NAME_FIELDS}
    }
    propertySubtype {
      ${ID_NAME_FIELDS}
    }
    market {
      ${ID_NAME_FIELDS}
    }
    submarket {
      ${ID_NAME_FIELDS}
    }
    micromarket {
      ${ID_NAME_FIELDS}
    }
    propertyClass {
      ${ID_NAME_FIELDS}
    }
  }
  ${PROPERTY_COUNTRY_FRAGMENT}
`;

export const PROPERTY_BASIC_DATA_FRAGMENT = gql`
  fragment propertyBasicDataFragment on Property {
    ${PROPERTY_MAIN_FIELDS}
    propertyCountry {
      ...propertyCountryFragment
    }
    expenses {
      ...expensesFragment
    }
    propertyType {
      ${ID_NAME_FIELDS}
    }
    propertySubtype {
      ${ID_NAME_FIELDS}
    }
    tenancyType {
      ${ID_NAME_FIELDS}
    }
    market {
      ${ID_NAME_FIELDS}
    }
    submarket {
      ${ID_NAME_FIELDS}
    }
    micromarket {
      ${ID_NAME_FIELDS}
    }
    propertyClass {
      ${ID_NAME_FIELDS}
    }
    locationType {
      ${ID_NAME_FIELDS}
    }
    status {
      ${ID_NAME_FIELDS}
    }
    owners {
      ...ownersFragment
    }
    longLeaseHolders {
      ...longLeaseHoldersFragment
    }
    businessPark {
      ${ID_NAME_FIELDS}
    }
    brokeGroundDate
    demolishedConverted
    renovatedYears {
      year
    }
    industrial {
      ...industrialFragment
    }
    ios {
      ...iosFragment
    }
  }
  ${PROPERTY_COUNTRY_FRAGMENT}
  ${EXPENSES_FRAGMENT}
  ${OWNERS_FRAGMENT}
  ${LONG_LEASE_HOLDERS_FRAGMENT}
  ${INDUSTRIAL_FRAGMENT}
  ${IOS_FRAGMENT}
`;

export const PROPERTY_COMPLETE_DATA_FRAGMENT = gql`
  fragment propertyCompleteDataFragment on Property {
    amenities {
      ${ID_NAME_FIELDS}
    }
    columnSpacingMin
    columnSpacingMax
    columnSpacingMinMt
    columnSpacingMaxMt
    parkingSpots
    parkingRatio
    buildDate
    brokeGroundDate
    convertedFromPropertyId
    demolishedConverted
    builtYear
    buildingSize
    buildingSizeMt
    city
    state
    country
    postalCode
    currencyCode
    createdAt
    updatedAt
    createdUser {
      ...createdUserFragment
    }
    updatedUser {
      ...updatedUserFragment
    }
    description
    expense {
      ...expenseFragment
    }
    floorPlateSize
    floorPlateSizeMt
    id
    healthcare {
      ...healthcareFragment
    }
    siteSize
    siteSizeSf
    siteSizeMt
    siteSizeHectares
    zoning
    industrial {
      ...industrialFragment
    }
    ios {
      ...iosFragment
    }
    multifamily {
      ...multifamilyFragment
    }
    dataCenter {
      ...dataCenterFragment
    }
    latitude
    latitudeDisplay
    leasingCompany {
      ...leasingCompanyFragment
    }
    architectCompany {
      ${ID_NAME_FIELDS}
    }
    uarn
    currentYearTaxAssessment
    well
    numberOfElevators
    longitude
    longitudeDisplay
    market {
      ${ID_NAME_FIELDS}
    }
    measurementSystem
    micromarket {
      ${ID_NAME_FIELDS}
    }
    name
    office {
      ...officeFragment
    }
    lifeScience {
      ...lifeScienceFragment
    }
    constructionType {
      name
    }
    owners {
      ...ownersFragment
    }
    businessPark {
      name
    }
    longLeaseHolders {
      ...longLeaseHoldersFragment
    }
    developmentCompanies {
      ...developmentCompaniesFragment
    }
    managementCompany {
      ...managementCompanyFragment
    }
    operatorCompany {
      ...operatorCompanyFragment
    }
    primaryAddress
    secondaryAddress
    tertiaryAddress
    quaternaryAddress
    numberOfAlternativeAddresses
    displayAddress
    propertyCountry {
      ...propertyCountryFragment
    }
    propertyClass {
      ${ID_NAME_FIELDS}
    }
    propertyType {
      ${ID_NAME_FIELDS}
    }
    propertySubtype {
      ${ID_NAME_FIELDS}
    }
    propertySubtypeDetail {
      ${ID_NAME_FIELDS}
    }
    tenancyType {
      ${ID_NAME_FIELDS}
    }
    locationType {
      ${ID_NAME_FIELDS}
    }
    ownerOccupied {
      ${ID_NAME_FIELDS}
    }
    renovatedYears {
      id
      year
    }
    siteSize
    siteSizeHectares
    siteCoverageRatio
    status {
      ${ID_NAME_FIELDS}
    }
    stories
    submarket {
      ${ID_NAME_FIELDS}
    }
    type
    coverMediaType
    coverMediaUrl
    thirdPartyId
    includeInStats
    expenses {
      ...expensesFragment
    }
  }
  ${PROPERTY_COUNTRY_FRAGMENT}
  ${EXPENSES_FRAGMENT}
  ${DATA_CENTER_FRAGMENT}
  ${LEASING_COMPANY_FRAGMENT}
  ${LIFE_SCIENCE_FRAGMENT}
  ${OWNERS_FRAGMENT}
  ${LONG_LEASE_HOLDERS_FRAGMENT}
  ${DEVELOPMENT_COMPANIES_FRAGMENT}
  ${MANAGEMENT_COMPANY_FRAGMENT}
  ${OPERATOR_COMPANY_FRAGMENT}
  ${CREATED_USER_FRAGMENT}
  ${UPDATED_USER_FRAGMENT}
  ${EXPENSE_FRAGMENT}
  ${HEALTHCARE_FRAGMENT}
  ${MULTIFAMILY_FRAGMENT}
  ${OFFICE_FRAGMENT}
  ${INDUSTRIAL_FRAGMENT}
  ${IOS_FRAGMENT}
`;

export const PROPERTY_COMPLETE_DATA_FRAGMENT_WITHOUT_COMMENTS = gql`
  fragment propertyCompleteDataFragment on Property {
    internalComments
    parkingComments
    amenities {
      ${ID_NAME_FIELDS}
    }
    certificationObjects {
      id
      objectTypeId
      objectType {
        ${ID_NAME_FIELDS}
      }
      certificationType {
        thirdPartyTypeId
        thirdPartyType {
          id
          name
          objectTypeIdSum
        }
      }
      objectId
      certificationTypeId
    }
    isProposedConverted
    proposedConvertedPropertyType {
      ${ID_NAME_FIELDS}
    }
    parkingCostPerMonth
    grossUp
    additionalParking
    columnSpacingMax
    columnSpacingMaxMt
    columnSpacingMin
    columnSpacingMinMt
    parkingSpots
    parkingRatio
    buildDate
    brokeGroundDate
    convertedFromPropertyId
    demolishedConverted
    preLeasedPct
    preLeasedSm
    preLeasedSf
    preLeasedCustomSf
    preLeasedCustomSm
    preLeasedCustomPct
    builtYear
    buildingSize
    buildingSizeMt
    officeSize
    officeSizeMt
    warehouseSize
    warehouseSizeMt
    city
    state
    country
    postalCode
    currencyCode
    createdAt
    updatedAt
    createdUser {
      ...createdUserFragment
    }
    updatedUser {
      ...updatedUserFragment
    }
    description
    expense {
      ...expenseFragment
    }
    floorPlateSize
    floorPlateSizeMt
    id
    healthcare {
      ...healthcareFragment
    }
    siteSize
    siteSizeSf
    siteSizeMt
    siteSizeHectares
    zoning
    multifamily {
      ...multifamilyFragment
    }
    dataCenter {
      ...dataCenterFragment
    }
    latitude
    latitudeDisplay
    leasingCompany {
      ...leasingCompanyFragment
    }
    architectCompany {
      ${ID_NAME_FIELDS}
    }
    uarn
    currentYearTaxAssessment
    well
    numberOfElevators
    longitude
    longitudeDisplay
    market {
      ${ID_NAME_FIELDS}
    }
    measurementSystem
    micromarket {
      ${ID_NAME_FIELDS}
    }
    name
    office {
      ...officeFragment
    }
    lifeScience {
      ...lifeScienceFragment
    }
    constructionType {
      name
    }
    owners {
      ...ownersFragment
    }
    businessPark {
      name
    }
    longLeaseHolders {
      ...longLeaseHoldersFragment
    }
    developmentCompanies {
      ...developmentCompaniesFragment
    }
    managementCompany {
      ...managementCompanyFragment
    }
    operatorCompany {
      ...operatorCompanyFragment
    }
    primaryAddress
    secondaryAddress
    tertiaryAddress
    quaternaryAddress
    numberOfAlternativeAddresses
    displayAddress
    propertyCountry {
      ...propertyCountryFragment
    }
    propertyClass {
      ${ID_NAME_FIELDS}
    }
    propertyType {
      ${ID_NAME_FIELDS}
    }
    propertySubtype {
      ${ID_NAME_FIELDS}
    }
    propertySubtypeDetail {
      ${ID_NAME_FIELDS}
    }
    tenancyType {
      ${ID_NAME_FIELDS}
    }
    locationType {
      ${ID_NAME_FIELDS}
    }
    ownerOccupied {
      ${ID_NAME_FIELDS}
    }
    renovatedYears {
      id
      year
    }
    siteSize
    siteSizeHectares
    siteCoverageRatio
    status {
      ${ID_NAME_FIELDS}
    }
    stories
    submarket {
      ${ID_NAME_FIELDS}
    }
    type
    coverMediaType
    coverMediaUrl
    thirdPartyId
    includeInStats
    expenses {
      ...expensesFragment
    }
    industrial {
      ...industrialFragment
    }
    ios {
      ...iosFragment
    }
  }
  ${PROPERTY_COUNTRY_FRAGMENT}
  ${EXPENSES_FRAGMENT}
  ${DATA_CENTER_FRAGMENT}
  ${LEASING_COMPANY_FRAGMENT}
  ${LIFE_SCIENCE_FRAGMENT}
  ${OWNERS_FRAGMENT}
  ${LONG_LEASE_HOLDERS_FRAGMENT}
  ${DEVELOPMENT_COMPANIES_FRAGMENT}
  ${MANAGEMENT_COMPANY_FRAGMENT}
  ${OPERATOR_COMPANY_FRAGMENT}
  ${CREATED_USER_FRAGMENT}
  ${UPDATED_USER_FRAGMENT}
  ${EXPENSE_FRAGMENT}
  ${HEALTHCARE_FRAGMENT}
  ${MULTIFAMILY_FRAGMENT}
  ${OFFICE_FRAGMENT}
  ${INDUSTRIAL_FRAGMENT}
  ${IOS_FRAGMENT}
`;

export const PROPERTY_COMP_SET_FRAGMENT = gql`
  fragment propertyCompSetFragment on Property {
    amenities {
      ${ID_NAME_FIELDS}
    }
    columnSpacingMax
    columnSpacingMaxMt
    columnSpacingMin
    columnSpacingMinMt
    builtYear
    buildingSizeMt
    floorPlateSizeMt
    buildingSize
    country
    city
    state
    currencyCode
    coverMediaType
    coverMediaUrl
    dataCenter {
      ...dataCenterFragment
    }
    displayAddress
    expenses {
      ...expensesFragment
    }
    floorPlateSize
    id
    siteSize
    siteSizeSf
    siteSizeMt
    siteSizeHectares
    zoning
    latitude
    latitudeDisplay
    longitude
    longitudeDisplay
    leasingCompany {
      ...leasingCompanyFragment
    }
    lifeScience {
      ...lifeScienceFragment
    }
    market {
      ${ID_NAME_FIELDS}
    }
    measurementSystem
    micromarket {
      ${ID_NAME_FIELDS}
    }
    multifamily {
      ...multifamilyFragment
    }
    numberOfAlternativeAddresses
    name
    office {
      ...officeFragment
    }
    owners {
      ...ownersFragment
    }
    parkingRatio
    primaryAddress
    propertyClass {
      ${ID_NAME_FIELDS}
    }
    propertyType {
      ${ID_NAME_FIELDS}
    }
    propertySubtype {
      ${ID_NAME_FIELDS}
    }
    renovatedYears {
      id
      year
    }
    quaternaryAddress
    secondaryAddress
    siteCoverageRatio
    submarket {
      ${ID_NAME_FIELDS}
    }
    tertiaryAddress
    industrial {
      ...industrialFragment
    }
    ios {
      ...iosFragment
    }
  }
  ${DATA_CENTER_FRAGMENT}
  ${EXPENSES_FRAGMENT}
  ${LEASING_COMPANY_FRAGMENT}
  ${LIFE_SCIENCE_FRAGMENT}
  ${OWNERS_FRAGMENT}
  ${MULTIFAMILY_FRAGMENT}
  ${OFFICE_FRAGMENT}
  ${INDUSTRIAL_FRAGMENT}
  ${IOS_FRAGMENT}
`;
